import type { ClaimRepairEntry } from './claimIntakeUtils';
import { normalizeRepairOrderDateToMdy } from './claimIntakeUtils';

export type ClaimGenerationThreeCsEntry = {
  complaint: string;
  cause: string;
  correction: string;
  labor_lines: {
    labor_amount: string;
    labor_description: string;
    labor_hours?: string;
    labor_op_code?: string;
  }[];
  part_lines: {
    part_amount: string;
    part_description: string;
    part_number: string;
    part_quantity: string;
    part_unit_price: string;
  }[];
};

/** Fields merged into `form_state` for the `3csmapper` workflow. */
export type ClaimGenerationFormStatePayload = {
  '3Cs': ClaimGenerationThreeCsEntry[];
  Invoice_Number: string;
  MILEGE_IN: string;
  Vehicle_Identification_Number: string;
  customer_name: string;
  customer_phone_number: string;
  customer_address: string;
  invoice_generator_address: string;
  invoice_generator_name: string;
  file_name: string | null;
  product_type: string;
  contract_number: string;
  repair_order_date: string;
  source_id: string | null;
  vehicle_arrival_method: string;
  vehicle_at_service_center: string;
  reimbursement_method: string;
  service_center_information: {
    servicer_name: string;
    physical_address: string;
    billing_address: string;
    contact_email: string;
    labor_rate: string;
  };
};

function trimLaborLine(row: ClaimRepairEntry['labor_lines'][number]) {
  return {
    labor_amount: row.labor_amount.trim(),
    labor_description: row.labor_description.trim(),
    ...(row.labor_hours.trim() ? { labor_hours: row.labor_hours.trim() } : {}),
    ...(row.labor_op_code.trim() ? { labor_op_code: row.labor_op_code.trim() } : {}),
  };
}

function trimPartLine(row: ClaimRepairEntry['part_lines'][number]) {
  return {
    part_amount: row.part_amount.trim(),
    part_description: row.part_description.trim(),
    part_number: row.part_number.trim(),
    part_quantity: row.part_quantity.trim(),
    part_unit_price: row.part_unit_price.trim(),
  };
}

export function buildClaimGenerationFormState(params: {
  repairs: ClaimRepairEntry[];
  arrivalMethod: string;
  atServiceCenter: string;
  reimbursement: string;
  servicerName: string;
  physicalAddress: string;
  billingAddress: string;
  contactEmail: string;
  laborRate: string;
  invoiceNumber: string;
  mileageIn: string;
  vehicleVin: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  repairOrderDate: string;
  contractNumber: string;
  productTypeCode: string;
  sourceId: string | null;
  fileName: string | null;
}): ClaimGenerationFormStatePayload {
  const threeCs: ClaimGenerationThreeCsEntry[] = params.repairs.map((r) => ({
    complaint: r.complaint.trim(),
    cause: r.cause.trim(),
    correction: r.correction.trim(),
    labor_lines: r.labor_lines.map(trimLaborLine),
    part_lines: r.part_lines.map(trimPartLine),
  }));

  return {
    '3Cs': threeCs,
    Invoice_Number: params.invoiceNumber.trim(),
    MILEGE_IN: params.mileageIn.trim(),
    Vehicle_Identification_Number: params.vehicleVin.trim(),
    customer_name: params.customerName.trim(),
    customer_phone_number: params.customerPhone.trim(),
    customer_address: params.customerAddress.trim(),
    invoice_generator_address: params.physicalAddress.trim() || params.billingAddress.trim(),
    invoice_generator_name: params.servicerName.trim(),
    file_name: params.fileName?.trim() ? params.fileName.trim() : null,
    product_type: params.productTypeCode.trim(),
    contract_number: params.contractNumber.trim(),
    repair_order_date: normalizeRepairOrderDateToMdy(params.repairOrderDate),
    source_id: params.sourceId?.trim() ? params.sourceId.trim() : null,
    vehicle_arrival_method: params.arrivalMethod,
    vehicle_at_service_center: params.atServiceCenter,
    reimbursement_method: params.reimbursement,
    service_center_information: {
      servicer_name: params.servicerName.trim(),
      physical_address: params.physicalAddress.trim(),
      billing_address: params.billingAddress.trim(),
      contact_email: params.contactEmail.trim(),
      labor_rate: params.laborRate.trim(),
    },
  };
}

export function isLaborLineNonEmpty(l: ClaimRepairEntry['labor_lines'][number]): boolean {
  return Boolean(
    l.labor_description.trim() ||
      l.labor_hours.trim() ||
      l.labor_op_code.trim()
  );
}

export function isPartLineNonEmpty(p: ClaimRepairEntry['part_lines'][number]): boolean {
  return Boolean(
    p.part_description.trim() ||
      p.part_number.trim() ||
      p.part_quantity.trim() ||
      p.part_unit_price.trim()
  );
}

/** Each repair needs at least one non-empty labor line or one non-empty part line. */
export function repairHasAtLeastOneLineItem(r: ClaimRepairEntry): boolean {
  return r.labor_lines.some(isLaborLineNonEmpty) || r.part_lines.some(isPartLineNonEmpty);
}

/**
 * True if this labor row can be removed. After removal, at least one non-empty labor or
 * part line must still exist on the repair (empty placeholder rows do not count).
 */
export function repairAllowsRemovingLaborLine(r: ClaimRepairEntry, lineIdx: number): boolean {
  const remainingLabor = r.labor_lines.filter((_, j) => j !== lineIdx);
  return remainingLabor.some(isLaborLineNonEmpty) || r.part_lines.some(isPartLineNonEmpty);
}

/** Same rule: keep at least one filled labor or part line on the repair. */
export function repairAllowsRemovingPartLine(r: ClaimRepairEntry, lineIdx: number): boolean {
  const remainingParts = r.part_lines.filter((_, j) => j !== lineIdx);
  return r.labor_lines.some(isLaborLineNonEmpty) || remainingParts.some(isPartLineNonEmpty);
}

/** @deprecated Use repairHasAtLeastOneLineItem */
export function repairHasNonEmptyLaborAndPart(r: ClaimRepairEntry): boolean {
  return repairHasAtLeastOneLineItem(r);
}
